import type { PermissionScope } from '@nexus/core';
import { Badge, type Breadcrumb, StatusBadge, toast } from '@nexus/ui';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import type { AccountRow, CatalogEntry } from './api';
import { adminSection, type AdminSection } from './sections';

/** Toast for a failed admin action, with the reason where the API names one. */
export function useAdminError(): (error: unknown, known?: Record<string, string>) => void {
  const { t } = useTranslation();
  return useCallback(
    (error, known = {}) => {
      let message = t('common.saveFailed');
      if (error instanceof ApiError) {
        if (known[error.message]) message = known[error.message]!;
        else if (error.message === 'version-conflict') message = t('admin.errors.version-conflict');
        else if (error.message === 'invalid-input') message = t('admin.errors.invalid-input');
        else if (error.status === 429) message = t('admin.errors.rate-limited');
        else if (error.status === 403) message = t('admin.errors.forbidden');
        else if (error.status === 404) message = t('admin.errors.notFound');
        else if (error.errorId) message = t('errors.serverError');
      }
      const errorId = error instanceof ApiError ? error.errorId : undefined;
      toast.error(message, errorId ? { description: t('errors.errorId', { id: errorId }) } : {});
    },
    [t],
  );
}

/** "Verwaltung › Konten" and further levels. */
export function useAdminBreadcrumbs(id: AdminSection['id'], ...rest: Breadcrumb[]): Breadcrumb[] {
  const { t } = useTranslation();
  const section = adminSection(id);
  return [
    { label: t('admin.title'), href: '/admin' },
    { label: t(`admin.sections.${id}.label`), href: rest.length ? section.path : undefined },
    ...rest,
  ];
}

/** Label of a permission or a pattern like `company.*`, from the texts of its owner. */
export function usePermissionText(): {
  label: (key: string, entry?: Pick<CatalogEntry, 'i18nKey'>) => string;
  description: (entry: Pick<CatalogEntry, 'i18nKey'>) => string | undefined;
  scope: (scope: PermissionScope | null) => string | undefined;
} {
  const { t, i18n } = useTranslation();
  return {
    label: (key, entry) => {
      if (key.endsWith('.*')) {
        const owner = key.slice(0, -2);
        return t('admin.roles.allOf') + ` (${ownerLabel(owner)})`;
      }
      const i18nKey = entry?.i18nKey ?? guessI18nKey(key);
      return i18n.exists(`${i18nKey}.label`) ? t(`${i18nKey}.label`) : key;
    },
    description: (entry) =>
      i18n.exists(`${entry.i18nKey}.description`) ? t(`${entry.i18nKey}.description`) : undefined,
    scope: (scope) => (scope ? t(`admin.roles.scopes.${scope}`) : undefined),
  };

  function ownerLabel(owner: string): string {
    if (i18n.exists(`admin.roles.owners.${owner}`)) return t(`admin.roles.owners.${owner}`);
    return i18n.exists(`${owner}.name`) ? t(`${owner}.name`) : owner;
  }
}

/** Kernel permissions are `<owner>.<rest>` with a one-segment owner. */
function guessI18nKey(key: string): string {
  const [owner, ...rest] = key.split('.');
  return `${owner}.permissions.${rest.join('.')}`;
}

/** Name of a role: its own name, or the translated name of the template it came from. */
export function useRoleName(): (role: {
  roleName?: string | null;
  name?: string | null;
  templateName?: string | null;
  roleTemplateName?: string | null;
}) => string {
  const { t } = useTranslation();
  return (role) => {
    const own = role.name ?? role.roleName;
    if (own) return own;
    const template = role.templateName ?? role.roleTemplateName;
    return template ? t(template) : '–';
  };
}

export function AccountStatus({ status }: { status: 'active' | 'locked' }) {
  const { t } = useTranslation();
  return status === 'active' ? (
    <StatusBadge status="success">{t('admin.accounts.status.active')}</StatusBadge>
  ) : (
    <StatusBadge status="danger">{t('admin.accounts.status.locked')}</StatusBadge>
  );
}

/** How an account logs in: SSO, password, pending invitation, authenticator app. */
export function LoginMethods({ account }: { account: Pick<AccountRow, 'sso' | 'login'> }) {
  const { t } = useTranslation();
  const { sso, login } = account;
  const invited = login.loginEmail !== null && !login.password;
  if (!sso && !login.password && !invited) {
    return <span className="text-fg-muted">{t('admin.accounts.login.none')}</span>;
  }
  return (
    <span className="flex flex-wrap gap-1.5">
      {sso && <Badge icon="cloud">{t('admin.accounts.login.sso')}</Badge>}
      {login.password && <Badge icon="key-round">{t('admin.accounts.login.password')}</Badge>}
      {invited && <Badge icon="mail">{t('admin.accounts.login.invited')}</Badge>}
      {login.twoFactor && <Badge icon="shield-check">{t('admin.accounts.login.twoFactor')}</Badge>}
      {login.passkeys > 0 && <Badge icon="fingerprint">{t('admin.accounts.login.passkey')}</Badge>}
    </span>
  );
}

/** Texts for the answers of the account endpoints that name a reason. */
export function useAccountErrors(): Record<string, string> {
  const { t } = useTranslation();
  return Object.fromEntries(
    [
      'account-locked',
      'cannot-reset-self',
      'login-email-taken',
      'mail-not-sent',
      'no-email',
      'password-login-disabled',
      'platform-account',
    ].map((code) => [code, t(`admin.accounts.errors.${code}`)]),
  );
}

export function PrivilegedBadge() {
  const { t } = useTranslation();
  return <Badge icon="shield">{t('admin.roles.privileged')}</Badge>;
}
